import React from 'react';
import { motion } from 'framer-motion';
import { Section, SectionHeader } from '../ui/Section';
import { Tabs } from '../ui/Tabs';
import { Accordion } from '../ui/Accordion';

export const FoodExperience = () => {
  const IMAGES = [
    "https://ik.imagekit.io/aeu02xvjgn/2020-06-10-Padua-Menu-0292.jpg?updatedAt=1776899481277",
    "https://ik.imagekit.io/aeu02xvjgn/2020-06-10-Padua-Menu-0250.jpg?updatedAt=1776899470068",
    "https://ik.imagekit.io/aeu02xvjgn/2020-06-10-Padua-Menu-0130.jpg?updatedAt=1776895638659"
  ];

  const slideImages = [...IMAGES, ...IMAGES];

  const horsCategories = [
    { title: "$9.00 Per Person Selections", content: "Classic options like Vegetable Spring Rolls, Tomato Bruschetta, and Savory Tartlets." },
    { title: "$9.50 Per Person Selections", content: "Popular choices including Chicken Skewers, Beef Sliders, and Artisanal Meatballs." },
    { title: "$10.50 Per Person Selections", content: "Elevated selections such as Pork Empanadas and Stuffed Mushrooms." },
    { title: "$11.00 Per Person Selections", content: "Premium Chef-curated bites and seasonal specialties." },
    { title: "$12.50 Per Person Selections", content: "Gourmet offerings including Mini Crab Cakes and Shrimp Cocktails." },
    { title: "Market Price Seafood Selections", content: "Fresh seasonal catches including Oysters and Lobster bites." }
  ];

  const stationaryItems = [
    {
      title: "Plattered Appetizers (Per Person)",
      content: (
        <ul className="space-y-2">
          <li>Fresh Vegetable Platter w/ dipping sauce: $9.00</li>
          <li>Fresh Fruit Platter: $9.50</li>
          <li>Assorted Cheese Platter w/ gourmet crackers: $12.00</li>
          <li>Charcuterie Grazing Table: $20.00</li>
          <li>Combo (Veg, Fruit, Cheese/Crackers): $30.00</li>
        </ul>
      )
    },
    { title: "Late Night Snacks ($15.00/pp)", content: "Choose from Nacho Bar, Street Taco Bar, or French Fry Bar." }
  ];

  const themedBuffets = [
    {
      title: "Themed Buffet Tiers",
      content: (
        <ul className="space-y-2">
          <li>Brunch Buffet: $73.50/pp</li>
          <li>Kansas City BBQ / Mexican / Italian: $77.50/pp</li>
          <li>Southern Cuisine: $82.00/pp</li>
          <li>California Grill / Caribbean: $83.00/pp</li>
        </ul>
      )
    }
  ];

  const tabs = [
    {
      label: "Hors d'oeuvres",
      content: (
        <div className="space-y-6">
          <p className="text-sm text-deep-charcoal/70 font-light italic text-center max-w-2xl mx-auto mb-8">
            Hand-passed selections for cocktail hour. Our kitchen handles gluten, nuts, and other common allergens; while precautions are taken, we cannot guarantee items are completely free of allergens.
          </p>
          <Accordion items={horsCategories} />
        </div>
      )
    },
    {
      label: "Entrée Tiers",
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-warm-stone/20">
              <h4 className="font-serif text-xl text-olive-grove mb-2">Buffet Entrées</h4>
              <p className="text-xs text-deep-charcoal/60 mb-4 italic">Choose 2 (charged at higher-cost selection). Includes 4 sides, fruit platter, rolls, and butter.</p>
              <ul className="text-sm space-y-2 text-deep-charcoal/80">
                <li>Poultry: $73.50 – $75.50</li>
                <li>Beef: $75.50 – $87.00</li>
                <li>Pork: $75.00 – $76.50</li>
                <li>Pasta: ~$68.50</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-warm-stone/20">
              <h4 className="font-serif text-xl text-olive-grove mb-2">Sit-Down Entrées</h4>
              <p className="text-xs text-deep-charcoal/60 mb-4 italic">Choose 2 (charged at higher-cost selection). Includes starch, veg, soup/salad, and rolls.</p>
              <ul className="text-sm space-y-2 text-deep-charcoal/80">
                <li>Poultry: Starts at ~$74.00</li>
                <li>Beef/Pork/Lamb: $79.00 – $94.00</li>
                <li>Pasta/Vegetarian: Starts at ~$72.00</li>
                <li className="pt-2 text-[10px] italic">* Add chicken to pasta +$2.00. Seafood: Market Price.</li>
              </ul>
            </div>
          </div>
          <div className="p-4 bg-aged-ivory rounded-xl text-center">
            <p className="text-xs text-deep-charcoal/70">
              Buffet Sides: Salads (Choose 2), Potato/Rice (Choose 1), Vegetables (Choose 1). <br />
              <span className="italic">All buffets are double-sided to expedite service.</span>
            </p>
          </div>
        </div>
      )
    },
    {
      label: "Stationary & Themes",
      content: (
        <div className="space-y-6">
          <Accordion items={themedBuffets} />
          <Accordion items={stationaryItems} />
        </div>
      )
    }
  ];

  return (
    <Section id="food" bg="bg-vintage-white" className="overflow-hidden">
      <SectionHeader title="Dining Service Experiences" subtitle="Chef-curated menus designed for elegance and efficiency." />

      <div className="relative mb-16 w-full overflow-hidden">
        <motion.div
          className="flex gap-4"
          animate={{ x: [0, -1200] }}
          transition={{ x: { repeat: Infinity, repeatType: "loop", duration: 25, ease: "linear" } }}
          style={{ width: "fit-content" }}
        >
          {slideImages.map((url, idx) => (
            <div
              key={idx}
              className="relative w-[300px] md:w-[450px] h-64 md:h-80 rounded-2xl overflow-hidden shadow-soft flex-shrink-0"
            >
              <img src={url} alt={`Menu Item ${idx + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </motion.div>
        <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-vintage-white to-transparent z-10"></div>
        <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-vintage-white to-transparent z-10"></div>
      </div>

      <div className="max-w-5xl mx-auto px-6">
        <Tabs tabs={tabs} />
        <p className="mt-8 text-[10px] text-center text-deep-charcoal/50 italic tracking-wider uppercase">
          * Meat/Seafood subject to market fluctuations. Pricing based on higher-cost selection.
        </p>
      </div>
    </Section>
  );
};